import { items } from './items';
import { models } from './models';
import { programs } from './programs';
import { textures, dataTextures } from './textures';

const triggersToNames = {};

for (const itemName in items) {
    const item = items[itemName];
    const { triggers = [] } = item;

    let triggersIndex = -1;
    const triggersLen = triggers.length;
    while (++triggersIndex < triggersLen) {
        const trigger = triggers[triggersIndex];
        (triggersToNames[trigger] || (triggersToNames[trigger] = {}))[itemName] = true;
    }
}

const defaultSceneConfig = {
    id: 'default',
    camera: camera2D,
    options: createDefaultOptions,
    numRenderedSplits: 8,
    clientMidEdgeInterpolation: true,
    arcHeight: 0.2,
    render: [
        'pointpicking',
        'pointsampling',
        'midedgeculled',
        'edgepicking',
        'arrowculled',
        'arrowselected',
        'edgeselected',
        'arrowhighlight',
        'edgehighlight',
        'radialaxes',
        'pointculledoutline',
        'pointculled',
        'fullscreen',
        'fullscreenDummy',
        'fullscreenDark',
        'pointselectedoutline',
        'pointselected',
        'pointhighlightoutline',
        'pointhighlight'
    ]
};

export const scenes = {
    gis: generateScene(defaultSceneConfig, items, models, programs, textures),
    default: generateScene(defaultSceneConfig, items, models, programs, textures),
    transparent: generateScene(defaultSceneConfig, items, models, programs, textures),
    netflowStraight: generateScene(defaultSceneConfig, items, models, programs, textures)
};

function generateScene(scene, items, models, programs, textures) {
    const render = scene.render.slice();
    const createCamera = scene.camera;
    const createOptions = scene.options;

    return function generate(opts) {
        //instanced analytic-SDF edge layers (render, picking, hover/selection
        //overlays) are the DEFAULT; ?legacyRenderer keeps the CPU-expanded LINES
        //layers instead (opts.edgesdf arrives false)
        const edgesdfSwaps = {
            midedgeculled: 'edgesdf',
            edgepicking: 'edgesdfpicking',
            edgehighlight: 'edgesdfhighlight',
            edgeselected: 'edgesdfselected',
            arrowculled: 'edgesdfarrow',
            arrowhighlight: 'edgesdfarrowhighlight',
            arrowselected: 'edgesdfarrowselected'
        };
        //the depth-only node-disc prepass draws FIRST so edge/arrow fragments
        //under opaque node cores depth-fail; edgesdf scenes only
        const renderList = opts && opts.edgesdf
            ? reorderArrowsBeforeEdges(['pointdepth', ...render.map(name => edgesdfSwaps[name] || name)])
            : render;
        const generatedScene = renderList.reduce(
            (scene, name) => {
                const {
                    buffers: cBuffers,
                    textures: cTextures,
                    dataTextures: cDataTextures,
                    triggers: cTriggers,
                    targets: cTargets,
                    programs: cPrograms,
                    uniforms: cUniforms,
                    items: cItems,
                    modes: cModes,
                    models: cModels
                } = scene;

                const {
                    uniforms,
                    renderTarget,
                    bindings,
                    program,
                    otherBuffers
                } = (cItems[name] = items[name]);

                const { textures: pTextures } = (cPrograms[program] = programs[program]);

                //samplers are named after the texture they sample, so the
                //program's texture list IS the binding list (renderer-state
                //binds each sampler to the same-named scene texture)
                if (pTextures) {
                    for (const textureName of pTextures) {
                        cTextures[textureName] = textures[textureName];
                        if (textureName in dataTextures) {
                            cDataTextures[textureName] = true;
                        }
                    }
                }

                const iUniforms = (cUniforms[name] = {});
                for (const uniformName in uniforms) {
                    iUniforms[uniformName] = uniforms[uniformName].defaultValues;
                }

                for (const trigger in triggersToNames) {
                    const names = triggersToNames[trigger];
                    const triggers = cTriggers[trigger] || (cTriggers[trigger] = []);
                    if (name in names) {
                        triggers.push(name);
                    }
                }

                for (const bindingName in bindings) {
                    const bufferBinding = bindings[bindingName];
                    const [modelName, attributeName] = bufferBinding;
                    const buffer = models[modelName][attributeName];
                    const { datasource } = buffer;

                    if (datasource === 'VERTEX_INDEX') {
                        cModes[datasource] = 1;
                    } else if (datasource === 'EDGE_INDEX') {
                        cModes[datasource] = 2;
                    }

                    cBuffers[modelName] = true;
                    cModels[modelName] = { ...cModels[modelName], [attributeName]: buffer };
                }

                for (const bindingName in otherBuffers) {
                    const bufferBinding = otherBuffers[bindingName];
                    const [modelName, attributeName] = bufferBinding;
                    const buffer = models[modelName][attributeName];

                    cBuffers[modelName] = true;
                    cModels[modelName] = { ...cModels[modelName], [attributeName]: buffer };
                }

                if (renderTarget) {
                    if (renderTarget !== 'CANVAS') {
                        cTargets[renderTarget] = true;
                    }
                    cTextures[renderTarget] = textures[renderTarget];
                }

                return scene;
            },
            {
                ...scene,
                ...{
                    render: renderList,
                    camera: createCamera(),
                    options: createOptions(),
                    modes: {},
                    triggers: {},
                    items: {},
                    programs: {},
                    buffers: {},
                    textures: {},
                    dataTextures: {},
                    models: {},
                    uniforms: {},
                    targets: {}
                }
            }
        );

        generatedScene.modes = Object.values(generatedScene.modes).sort();
        generatedScene.targets = Object.keys(generatedScene.targets);
        generatedScene.buffers = Object.keys(generatedScene.buffers);
        generatedScene.dataTextures = Object.keys(generatedScene.dataTextures);

        return generatedScene;
    };
}

//Arrowheads render slightly nearer than the edge plane (see arrow.vertex.glsl)
//and must draw BEFORE the edge strokes: strokes then depth-fail beneath the
//arrowhead, which therefore occludes the stroke rather than double-blending
//with it. The overlay pairs already have arrows first; the main pass doesn't.
//TODO: once legacy is gone, bake arrow-before-edge into the render list and delete this helper.
function reorderArrowsBeforeEdges(list) {
    const out = list.slice();
    [
        ['edgesdfarrow', 'edgesdf'],
        ['edgesdfarrowhighlight', 'edgesdfhighlight'],
        ['edgesdfarrowselected', 'edgesdfselected']
    ].forEach(([arrowName, edgeName]) => {
        const arrowIndex = out.indexOf(arrowName);
        const edgeIndex = out.indexOf(edgeName);
        if (arrowIndex !== -1 && edgeIndex !== -1 && arrowIndex > edgeIndex) {
            out[arrowIndex] = edgeName;
            out[edgeIndex] = arrowName;
        }
    });
    return out;
}

function createDefaultOptions() {
    return {
        enable: [['BLEND'], ['DEPTH_TEST']],
        disable: [['CULL_FACE']],
        blendFuncSeparate: [['SRC_ALPHA', 'ONE_MINUS_SRC_ALPHA', 'ONE', 'ONE']],
        blendEquationSeparate: [['FUNC_ADD', 'FUNC_ADD']],
        depthFunc: [['LEQUAL']],
        //reset per item: the pointdepth prepass masks color writes off
        colorMask: [[true, true, true, true]],
        clearColor: [[51 / 255, 51 / 255, 57 / 255, 1.0]],
        lineWidth: [[1]]
    };
}

function camera2D() {
    return {
        type: '2d',
        bounds: {
            top: -1,
            left: 0,
            bottom: 0,
            right: 1
        },
        nearPlane: -1,
        farPlane: 10
    };
}
